import * as dateFns from "date-fns";
import { Locale } from "date-fns";

/**
 * Period rules of the CAN consumption dashboard.
 *
 * Invoice integration lags one to two days, so the volume of the most recent days is still
 * incomplete — measured in August, a complete day carries 79% to 98% of its travels with volume
 * while the last two carried 30% and 24%. Dividing fuel by that short volume doubles the consumption
 * per m³ out of nowhere. The default window therefore stops at D-2, and any day the backend flags as
 * still consolidating is marked on the chart.
 */

/** Days between today and the last day considered complete. */
export const CONSOLIDATION_LAG_DAYS = 2;

/** Length of the default window, in days. */
export const DEFAULT_PERIOD_DAYS = 7;

export interface DashboardPeriod {
  /** First day of the window, "yyyy-MM-dd" */
  startDate: string;
  /** Last day of the window, "yyyy-MM-dd" */
  finishDate: string;
}

const asDayString = (date: Date): string => dateFns.format(date, "yyyy-MM-dd");

/**
 * Window of `days` days ending on the last day considered complete (D-2).
 * @param days Length of the window
 * @param reference Day the user is looking at the screen — today, in production
 */
export const periodEndingOnLastCompleteDay = (days: number, reference: Date = new Date()): DashboardPeriod => {
  const finish = dateFns.subDays(reference, CONSOLIDATION_LAG_DAYS);
  const start = dateFns.subDays(finish, Math.max(days, 1) - 1);

  return { startDate: asDayString(start), finishDate: asDayString(finish) };
};

/** Default window of the dashboard: seven days ending on D-2. */
export const defaultPeriod = (reference: Date = new Date()): DashboardPeriod => (
  periodEndingOnLastCompleteDay(DEFAULT_PERIOD_DAYS, reference)
);

/** Turns the picked days into the ISO instants the endpoint expects, with the local time zone. */
export const toRequestRange = (period: DashboardPeriod): { startDate: string; finishDate: string } => ({
  startDate: `${period.startDate}T00:00:00`,
  finishDate: `${period.finishDate}T23:59:59`
});

/** Label of one day of the series: weekday plus day of month, in the active language. */
export const formatDayLabel = (isoDate: string, locale?: Locale): string => {
  const date = dateFns.parseISO(isoDate);

  if (!dateFns.isValid(date)) return isoDate;

  return `${dateFns.format(date, "EEE", { locale })} ${dateFns.format(date, "dd")}`;
};

/** Human label of the whole window, used on the period line. */
export const formatPeriodLabel = (period: DashboardPeriod, locale?: Locale): string => {
  const start = dateFns.parseISO(period.startDate);
  const finish = dateFns.parseISO(period.finishDate);

  if (!dateFns.isValid(start) || !dateFns.isValid(finish)) return `${period.startDate} — ${period.finishDate}`;

  const pattern = "dd/MM/yyyy";

  if (period.startDate === period.finishDate) return dateFns.format(start, pattern, { locale });

  return `${dateFns.format(start, pattern, { locale })} — ${dateFns.format(finish, pattern, { locale })}`;
};

/**
 * First and last instants of the window, in the local time zone, as the filter drawer expects.
 * @param period Window the panel is showing
 */
export const toDateTimeRange = (period: DashboardPeriod): { startDate: string; finishDate: string } => {
  const pattern = "yyyy-MM-dd'T'HH:mm:ssxxx";

  return {
    startDate: dateFns.format(dateFns.startOfDay(dateFns.parseISO(period.startDate)), pattern),
    finishDate: dateFns.format(dateFns.endOfDay(dateFns.parseISO(period.finishDate)), pattern)
  };
};

/**
 * Window the panel takes from the instants the filter drawer gives back.
 *
 * Every point of the panel is a whole day, so only the day of each instant survives. An inverted
 * range is reordered instead of reaching the endpoint, which would answer an empty panel.
 * @param startDate First instant picked in the drawer
 * @param finishDate Last instant picked in the drawer
 */
export const fromDateTimeRange = (startDate: string, finishDate: string): DashboardPeriod => {
  const start = startDate.slice(0, 10);
  const finish = finishDate.slice(0, 10);

  return start <= finish
    ? { startDate: start, finishDate: finish }
    : { startDate: finish, finishDate: start };
};
